import React from 'react';
import { Truck, MapPin, Shield, Clock, IndianRupee, Headphones } from 'lucide-react';

const ValueProps = () => {
  const features = [
    {
      icon: Truck,
      title: 'Fast & Reliable Delivery',
      description: 'We guarantee timely deliveries, ensuring your customers receive their packages promptly, boosting their trust in your brand.',
      color: 'from-blue-700 to-blue-800'
    },
    {
      icon: MapPin,
      title: 'Real-Time Tracking',
      description: 'Allow your customers to track their shipments in real-time, providing transparency and peace of mind during the delivery process.',
      color: 'from-blue-700 to-blue-800'
    },
    {
      icon: Shield,
      title: 'Secure Shipments',
      description: 'We prioritize safety with every delivery, offering secure shipping options that protect sensitive and valuable items.',
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      icon: IndianRupee,
      title: 'Flexible Payment Options',
      description: 'Provide your customers with a variety of payment options, including cash on delivery, to make the checkout process seamless.',
      color: 'from-blue-700 to-blue-800'
    },
    {
      icon: Headphones,
      title: '24/7 Customer Support',
      description: 'Our dedicated customer support team is available around the clock to assist with any questions or concerns about shipments.',
      color: 'from-blue-700 to-blue-800'
    },
    {
      icon: Clock,
      title: 'Optimized Shipping Solutions',
      description: 'We offer tailored shipping solutions that optimize delivery routes and reduce shipping costs, ensuring efficiency at every step.',
      color: 'from-emerald-500 to-emerald-600'
    }
  ];

  return (
    <section className="relative py-24 bg-white overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent"></div>
        
        {/* Subtle gradient orbs */}
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-gradient-to-br from-blue-100/30 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-gradient-to-br from-blue-100/30 to-transparent rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-blue-800 font-semibold text-sm uppercase tracking-wide">Explore the Excellence</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-4 text-gray-900">
            Features That Keep You <span className="text-blue-800">Ahead</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Enhance customer experience with features that keep your business ahead of the competition
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div 
                key={index} 
                className="relative group p-8 bg-gradient-to-br from-white to-blue-50/30 rounded-2xl border border-gray-200 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 overflow-hidden"
              >
                {/* Decorative corner gradient - Fixed overflow */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/40 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10">
                  <div className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>

                {/* Hover indicator - Constrained within card */}
                <div className={`absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r ${feature.color} group-hover:w-full transition-all duration-300 rounded-b-2xl`}></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ValueProps;